import {
  badgeClasses,
  inputAdornmentClasses,
  inputBaseClasses,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import {stepLabelClasses} from '@mui/material/StepLabel';
import {createTheme} from '@mui/material/styles';
import {gridClasses} from '@mui/x-data-grid-pro';
import {enUS, nbNO} from '@mui/x-data-grid-pro/locales';
import '@mui/x-data-grid-pro/themeAugmentation';
import '@mui/x-data-grid/themeAugmentation';
import '@mui/x-date-pickers-pro/themeAugmentation';
import {treeItemClasses} from '@mui/x-tree-view';
import '@mui/x-tree-view-pro/themeAugmentation';
import '@mui/x-tree-view/themeAugmentation';
import type {Locale} from 'localization/types';
import {MasterLocale} from 'localization/types';
import {DropDownIcon} from 'components/icons/DropDownIcon';
import {BaseColorPalette, ColorPalette} from './colorPalette';
import {Fonts, LabelFont} from './fonts';
import {px2rem} from './px2rem';
import {UXUnit} from './settings';
import './themeAugmentation';

export const createCustomTheme = (locale: Locale | undefined = MasterLocale) =>
  createTheme(
    {
      breakpoints: {
        values: {
          xs: 0,
          sm: 1040,
          md: 1200,
          lg: 2000,
          xl: 2600,
        },
      },
      typography: {
        fontFamily: ['ABCMonumentGrotesk', 'sans-serif'].join(','),
        ...Fonts,
      },
      spacing: UXUnit,
      palette: {
        background: {
          default: ColorPalette.beige,
          paper: BaseColorPalette.offWhite,
          inner: BaseColorPalette.lightBeige,
          // same as defaults
          beige: {
            level1: BaseColorPalette.beige,
            level2: BaseColorPalette.offWhite,
            level3: BaseColorPalette.lightBeige,
          },
          brown: {
            level1: BaseColorPalette.brown,
            level2: BaseColorPalette.darkBrown,
            level3: BaseColorPalette.enhancedBrown,
          },
          burgundy: {
            level1: BaseColorPalette.burgundy50,
            level2: BaseColorPalette.burgundy75,
            level3: BaseColorPalette.burgundy,
          },
        },
        primary: {
          main: ColorPalette.burgundy,
          light: ColorPalette.offWhite,
          dark: ColorPalette.burgundy,
          contrastText: ColorPalette.offWhite,
        },
        secondary: {
          main: ColorPalette.textSecondary, //  ColorPalette.lightBeige,
          light: ColorPalette.offWhite,
          dark: ColorPalette.brown,
          contrastText: ColorPalette.burgundy,
        },
        text: {
          primary: ColorPalette.textPrimary,
          secondary: ColorPalette.textSecondary,
          disabled: ColorPalette.burgundy50,
        },
        info: {
          main: ColorPalette.info60,
        },
        error: {
          main: ColorPalette.error,
        },
        warning: {
          main: ColorPalette.warning,
        },
        success: {
          main: ColorPalette.success,
        },
      },
      colors: BaseColorPalette,
      status: {
        new: '#CAE4EA',
        pre: '#FFCD9F',
        control: '#E1D7D1',
        done: ColorPalette.enhancedGreen,
        change: '#FFD4CB',
        mottak: '#EFA794',
        ml: ColorPalette.burgundy,
      },
      components: {
        MuiDesktopDatePicker: {
          defaultProps: {
            slotProps: {
              textField: {
                size: 'small',
                fullWidth: true,
                slotProps: {htmlInput: {autoComplete: 'off'}},
              },
            },
          },
        },
        MuiRichTreeViewPro: {
          styleOverrides: {
            root: ({theme}) => ({
              flexGrow: 1,
              flex: 1,
              marginRight: theme.spacing(0.25),
              [`& .${treeItemClasses.iconContainer}`]: {
                '& svg': {
                  fontSize: '24px',
                },
              },
              [`& .${treeItemClasses.label}`]: {
                '& .labelBox': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: `${theme.spacing(0.25)} 0`,
                  minHeight: '42px',
                  '& .outerBox': {
                    margin: `${theme.spacing(0.375)} 0 ${theme.spacing(0.375)} ${theme.spacing(0.375)}`,
                    borderRadius: theme.shape.borderRadius,
                    backgroundColor: ColorPalette.secondary.beige,
                    '& .nodeCode': {
                      padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
                      color: ColorPalette.burgundy,
                    },
                  },
                  '& .nodeText': {
                    paddingLeft: theme.spacing(0.5),
                    color: ColorPalette.primary.burgundy,
                  },
                  '& .nodeStatus': {
                    marginLeft: 'auto',
                    color: 'inherit',
                  },
                },
              },
              [`& .${treeItemClasses.content}`]: {
                marginTop: theme.spacing(0.5),
                borderRadius: Number(theme.shape.borderRadius) * 0.5,
                flex: 1,
                backgroundColor: `${ColorPalette.beige50}`,
                '&:hover': {
                  backgroundColor: `${ColorPalette.enhancedLightBeige}`,
                },
              },
              [`& [data-selected]`]: {
                backgroundColor: `${ColorPalette.secondary.beige}!important`,
                [`& .${treeItemClasses.content}`]: {
                  backgroundColor: `${ColorPalette.secondary.beige}!important`,
                  '&:hover': {
                    backgroundColor: `${ColorPalette.secondary.beige}!important`,
                  },
                  '&:focus': {
                    backgroundColor: `${ColorPalette.secondary.beige}!important`,
                  },
                },
              },
            }),
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: ({theme}) => ({
              [`& .${gridClasses.row}`]: {
                '--rowBorderColor': 'none',
                border: '0 solid white',
                outline: '1px solid white',
                borderRadius: Number(theme.shape.borderRadius) * 0.5,
                backgroundColor: '#f9faf8',
              },
              [`& .${gridClasses.cell}`]: {
                color: '#480905',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '11px',
                lineHeight: '15px',
              },
              /* Replace selected header outline with weak/strong color of title */
              [`& .${gridClasses.columnHeader}`]: {
                color: '#9a9d90',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '9px',
                lineHeight: '9px',
                letterSpacing: '0.399561px',
                textTransform: 'uppercase',
              },
              /* Hide separator */
              [`& .${gridClasses.columnSeparator}`]: {
                color: 'white',
              },
              /* Remove cell bottom borders */
              [`& .${gridClasses.columnHeaders}, .${gridClasses.columnHeader}, .${gridClasses.cell}, .${gridClasses.filler}`]:
                {
                  borderBottomWidth: '0 !important',
                },
              [`& .${gridClasses.toolbar}`]: {
                borderBottom: 'none',
              },
              [`& .${gridClasses.toolbarContainer} .${badgeClasses.badge}`]: {
                backgroundColor: '#480905',
                fontSize: '.6em',
              },
              [`& .${gridClasses.footerContainer}`]: {
                borderTop: 'none',
              },

              [`& .${gridClasses['columnHeader--sorted']}, .${gridClasses.columnHeader}:hover, .${gridClasses.columnHeader}:focus, .${gridClasses.cell}, .${gridClasses.cell}:focus`]:
                {
                  outline: 'solid transparent 0 !important',
                  color: '#480905',
                },
              [`& .${gridClasses.columnHeaderTitleContainer} > .${gridClasses.iconButtonContainer}`]:
                {
                  visibility: 'collapse !important',
                  width: '0 !important',
                },
              [`& .${gridClasses.columnHeaderTitleContainer} .${gridClasses.columnHeaderTitle}`]:
                {
                  zIndex: 999,
                },
            }),
          },
        },
        MuiBadge: {
          styleOverrides: {
            badge: {
              background: ColorPalette.offWhite,
              ...Fonts.body2,
            },
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: {
              '@media (min-width: 0px)': {
                minHeight: px2rem(55),
              },
            },
          },
        },
        MuiButton: {
          defaultProps: {
            disableRipple: true,
            //disableElevation: true,
            size: 'medium',
            variant: 'contained',
            loadingPosition: 'start',
          },
          styleOverrides: {
            root: ({theme}) => ({
              variants: [
                {
                  props: {variant: 'contained'},
                  style: {
                    borderRadius: 999,
                    borderStyle: 'solid',
                    borderWidth: '1.5px',
                    borderColor: ColorPalette.terracotta,
                  },
                },
                {
                  props: {variant: 'contained', color: 'success'},
                  style: {
                    backgroundColor: theme.colors.green2,
                    '&.Mui-disabled': {
                      backgroundColor: theme.colors.green2,
                      opacity: 0.8,
                    },
                  },
                },
                {
                  props: {variant: 'contained', color: 'primary'},
                  style: {
                    backgroundColor: ColorPalette.brown,
                    color: ColorPalette.primary.burgundy,
                    '&.Mui-disabled': {
                      backgroundColor: ColorPalette.brown,
                      opacity: 0.8,
                    },
                  },
                },
                {
                  props: {variant: 'contained', color: 'secondary'},
                  style: {
                    backgroundColor: ColorPalette.offWhite,
                    color: ColorPalette.primary.burgundy,
                    '&.Mui-disabled': {
                      backgroundColor: ColorPalette.offWhite,
                    },
                  },
                },
                {
                  props: {size: 'medium'},
                  style: {
                    padding: `0 ${theme.spacing(3)}`,
                    height: theme.spacing(6),
                  },
                },
              ],
            }),
          },
        },
        MuiIconButton: {
          defaultProps: {
            loadingIndicator: <CircularProgress />,
          },
          styleOverrides: {
            root: ({theme}) => ({
              ':hover': {
                borderRadius: Number(theme.shape.borderRadius) * 2,
              },
              borderRadius: Number(theme.shape.borderRadius) * 2,
            }),
          },
        },
        MuiPickersInputBase: {
          styleOverrides: {
            root: ({theme}) => ({
              color: theme.palette.text.primary,
              [`&.MuiPickersInputBase-colorWarning`]: {
                boxShadow: `0 0 0 1.25px ${theme.palette.warning.main}`,
                ':focus-within, :hover, :active': {
                  boxShadow: `0 0 0 2px ${theme.palette.warning.main}`,
                },
                [`& .MuiPickersOutlinedInput-notchedOutline`]: {
                  border: 0,
                },
              },
              [`&.MuiPickersInputBase-colorError`]: {
                boxShadow: `0 0 0 1.25px ${theme.palette.error.main}`,
                ':focus-within, :hover, :active': {
                  boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
                },
                [`& .MuiPickersOutlinedInput-notchedOutline`]: {
                  border: 0,
                },
              },
            }),
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: ({theme}) => ({
              paddingBottom: `${theme.spacing(1)}!important`,
              ...LabelFont,
              color: theme.palette.text.secondary,

              [`&.MuiFormLabel-colorWarning`]: {
                color: theme.palette.warning.main,
                fontWeight: 'bold',
              },
              [`&.MuiFormLabel-colorError`]: {
                color: theme.palette.error.main,
                fontWeight: 'bold',
              },
            }),
          },
        },
        // Metadata input styling
        MuiInputBase: {
          styleOverrides: {
            root: ({theme}) => ({
              ...Fonts.body1,
              border: 'none',
              boxShadow: `0 0 0 1.25px ${theme.colors.warmGreen}`,
              outline: 'none',
              transition: '.1s',
              borderRadius: theme.shape.borderRadius,
              ':focus-within, :hover, :active': {
                boxShadow: `0 0 0 2px ${theme.colors.warmGreen}`,
              },
              [`&.MuiInputBase-colorWarning`]: {
                boxShadow: `0 0 0 1.25px ${theme.palette.warning.main}`,
                ':focus-within, :hover, :active': {
                  boxShadow: `0 0 0 2px ${theme.palette.warning.main}`,
                },
              },
              [`&.MuiInputBase-colorError`]: {
                boxShadow: `0 0 0 1.25px ${theme.palette.error.main}`,
                ':focus-within, :hover, :active': {
                  boxShadow: `0 0 0 2px ${theme.palette.error.main}`,
                },
              },
              '&.MuiOutlinedInput-root': {
                '&.Mui-disabled': {
                  opacity: 0.8,
                },
              },
            }),
            input: {
              color: ColorPalette.primary.burgundy,
              '::selection': {
                background: ColorPalette.primary.lightGreen,
              },
              '::placeholder': {
                color: ColorPalette.primary.warmGreen,
              },
            },
            [`&.${inputBaseClasses.input}.${inputBaseClasses.multiline}`]: {
              color: ColorPalette.primary.burgundy,
              padding: '0!important',
              '::selection': {
                background: ColorPalette.primary.lightGreen,
              },
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              [`&.${inputAdornmentClasses.positionEnd}`]: {
                marginLeft: '0',
              },
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: ({theme}) => ({
              // Same padding t/r/b/l to have symmetric, circular checkbox hover
              padding: `${theme.spacing(0.375)}!important`,
              // Use margins to adjust position
              margin: `0 0 0 ${theme.spacing(0.25)}!important`,
            }),
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: ({theme}) => ({
              // Same padding t/r/b/l to have symmetric, circular hover
              padding: `${theme.spacing(0.5)}!important`,
              // Use margins to adjust position
              margin: `0 ${theme.spacing(0.25)} 0 ${theme.spacing(0.5)}!important`,
            }),
          },
        },
        // Metadata input styling
        MuiFormControl: {
          styleOverrides: {
            root: {
              background: ColorPalette.primary.white,
            },
          },
        },
        MuiFormControlLabel: {
          styleOverrides: {
            label: {
              ...LabelFont,
            },
          },
        },
        // Metadata input styling
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              border: 'none',
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: ({theme}) => ({
              ...Fonts.body1,
              borderRadius: theme.shape.borderRadius,
            }),
            label: {
              overflow: 'visible',
            },
          },
        },
        MuiAvatar: {
          styleOverrides: {
            root: {
              // Hard code 24px to match icon sizes in svg
              width: '24px', // px2rem(24),
              height: '24px', // px2rem(24),
              background: 'transparent',
            },
          },
        },
        MuiAccordion: {
          defaultProps: {
            disableGutters: true,
          },
          styleOverrides: {
            root: ({theme}) => ({
              background: ColorPalette.secondary.lightBeige,
              boxShadow: 'none',
              borderRadius: Number(theme.shape.borderRadius) * 2,

              '&:before': {
                content: 'none',
              },

              '&.Mui-expanded': {
                margin: 0,
              },
              '&:not(:last-of-type)': {
                marginBottom: theme.spacing(2),
              },
            }),
          },
        },
        MuiAccordionSummary: {
          defaultProps: {
            expandIcon: <DropDownIcon />,
          },
          styleOverrides: {
            root: ({theme}) => ({
              minHeight: 6.5 * UXUnit,
              padding: 0,
              paddingLeft: theme.spacing(6.5),

              '&:before': {
                content: 'none',
              },
            }),
            content: {
              margin: 0,
            },
            expandIconWrapper: ({theme}) => ({
              position: 'absolute',
              left: theme.spacing(2),
              display: 'flex',
              alignItems: 'center',
              background: ColorPalette.beige,
              borderRadius: Number(theme.shape.borderRadius) * 2,

              '&:hover': {
                opacity: 0.8,
                background: ColorPalette.enhancedBeige,
              },
            }),
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: ({theme}) => ({
              borderRadius: theme.shape.borderRadius,
            }),
          },
        },
        MuiStep: {
          styleOverrides: {
            root: ({theme}) => ({
              marginRight: theme.spacing(2),
              padding: 0,
            }),
          },
        },
        MuiStepButton: {
          styleOverrides: {
            root: {
              margin: 0,
              padding: 0,
            },
          },
        },
        MuiStepLabel: {
          styleOverrides: {
            iconContainer: {
              padding: 0,
              visibility: 'hidden',
              width: 0,
            },
            label: ({theme}) => ({
              padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
              borderRadius: 9999,
              [`&.${stepLabelClasses.active}`]: {
                backgroundColor: ColorPalette.burgundy,
                color: ColorPalette.offWhite,
              },
            }),
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: ({theme}) => ({
              ...Fonts.body2,
              backgroundColor: ColorPalette.primary.burgundy,
              color: ColorPalette.offWhite,
              borderRadius: theme.shape.borderRadius,
              padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
            }),
            arrow: {
              color: ColorPalette.primary.burgundy,
            },
          },
          defaultProps: {
            arrow: true,
          },
        },
        MuiSnackbar: {
          styleOverrides: {
            root: ({theme}) => ({
              top: theme.spacing(4),
              borderRadius: Number(theme.shape.borderRadius) * 4,
            }),
          },
        },
        MuiAlert: {
          styleOverrides: {
            root: ({theme}) => ({
              borderRadius: Number(theme.shape.borderRadius) * 4,
            }),
          },
          variants: [
            {
              props: {severity: 'success'},
              style: {
                backgroundColor: ColorPalette.success,
              },
            },
            {
              props: {severity: 'error'},
              style: {
                backgroundColor: ColorPalette.error,
              },
            },
            {
              props: {severity: 'warning'},
              style: {
                backgroundColor: ColorPalette.warmGreen,
              },
            },
          ],
        },
      },
      shape: {
        borderRadius: UXUnit,
      },
    },
    locale === 'en' ? enUS : nbNO,
  );
